import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, APP_NAME, APP_URL } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const IntakePage = () => {
  const { shopSlug } = useParams();
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(`session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [completed, setCompleted] = useState(false);
  // const [infoCollected, setInfoCollected] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchShop();
  }, [shopSlug]);

  // Update page title
  useEffect(() => {
    document.title = `Intake Assistant - ${APP_NAME}`;
  }, []);

  const fetchShop = async () => {
    try {
      const response = await axios.get(`${API}/intake/shop/${shopSlug}`);
      setShop(response.data);

      // Add initial AI greeting
      setMessages([
        {
          role: "assistant",
          content: `Hi! Welcome to ${response.data.shop_name}. I'm here to help prepare for your appointment. Let's start - what's your name?`,
        },
      ]);
    } catch (error) {
      toast.error("Shop not found");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {

    e.preventDefault();
    let userMessage = input.trim();

    if (!userMessage || sending) return;

    setInput("");
    setMessages([...messages, { role: "user", content: userMessage }]);
    setSending(true);

    // Move scroll to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    // Check if user wants to finish
    if (userMessage.toLowerCase().includes("done")) {
      try {
        // setInfoCollected(false);
        await axios.post(`${API}/intake/submit`, {
          shop_slug: shopSlug,
          session_id: sessionId,
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Perfect! We've sent all your details to ${shop.shop_name}. They'll have items ready for your appointment. See you soon!`,
          },
        ]);
        // Move scroll to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        setCompleted(true);
      } catch (error) {
        // setInfoCollected(true);
        toast.error("Failed to submit intake");
      }
      setSending(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/intake/chat`, {
        shop_slug: shopSlug,
        session_id: sessionId,
        message: userMessage
      });

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);

      // Move scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

      // If all info collected
      if (response.data.completed) {
        // setInfoCollected(true);

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `That's everything I need to create your service sheet. If you'd like to add more details, just keep typing. If you're finished, reply "done" and I'll send this to ${shop.shop_name} now.`,
          },
        ]);

        // Move scroll to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center">
        <div className="text-[#f8f8f8]">Loading...</div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#f8f8f8] mb-2">Business Not Found</h1>
          <p className="text-[#f8f8f8]/70">Please check your link and try again.</p>
        </div>
      </div>
    );
  }

  if (!shop.service_types || shop.service_types.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#f8f8f8] mb-2">No Services Found</h1>
          <p className="text-[#f8f8f8]/70">No service types found. Please configure service types in your business settings.</p>
        </div>
      </div>
    );
  }

  if (shop.subscription_status === "unpaid" || shop.subscription_status === "canceled") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#f8f8f8] mb-2">Business Not Active</h1>
          <p className="text-[#f8f8f8]/70">Your subscription is not active. Please contact support for assistance.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f]">
        {/* Header */}
        <div className="bg-[#005f6b]/20 backdrop-blur-sm border-b border-[#d4af37]/20 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            {shop.logo_url ? (
              <img src={shop.logo_url} alt={shop.shop_name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-full" />
            )}
            <div>
              <h1 className="text-xl font-semibold text-[#d4af37]">{shop.shop_name}</h1>
              <p className="text-sm text-[#f8f8f8]/70">Pre-Appointment Intake</p>
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div className="max-w-3xl mx-auto p-6">
          <Card className="bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 overflow-hidden">
            {/* Messages */}
            <div className="h-[calc(100vh-250px)] md:h-[calc(100vh-220px)] overflow-y-auto p-6 space-y-4 relative" data-testid="chat-messages">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  data-testid={`message-${idx}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "flex-col justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-5 py-3 ${msg.role === "user"
                      ? "bg-[#00ced1] text-[#0a1a1f]"
                      : "bg-[#005f6b]/40 text-[#f8f8f8] border border-[#d4af37]/20"
                      }`}
                  >
                    {msg.content}
                  </div>
                  {/* {msg.content.includes("prepare for your appointment") && !serviceType && (
                    <div className="flex items-center gap-2 mt-2">
                      {shop.service_types.map((type, idx) => (
                        <Button
                          size="sm"
                          key={idx}
                          variant="outline"
                          className="text-[#f8f8f8] hover:text-[#d4af37] text-xs"
                          onClick={() => handleServiceType(type)}
                        >
                          {type.name}
                        </Button>
                      ))}
                    </div>
                  )} */}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-[#005f6b]/40 text-[#f8f8f8] border border-[#d4af37]/20 rounded-2xl px-5 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#00ced1] rounded-full animate-bounce" style={{ animationDelay: "0s" }}></span>
                      <span className="w-2 h-2 bg-[#00ced1] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
                      <span className="w-2 h-2 bg-[#00ced1] rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
              {/* {infoCollected && !completed && (
                <div className="sticky bottom-0 left-0 w-full bg-[#00ced1] text-[#ffffff] px-3 py-1 rounded-full text-sm font-medium border border-[#00ced1]/40">
                  All info collected! Type "done" to finish.
                </div>
              )} */}
            </div>

            {/* Input */}
            {!completed ? (
              <form onSubmit={sendMessage} className="p-6 border-t border-[#d4af37]/20 bg-[#005f6b]/10">
                <div className="flex gap-3">
                  <Input
                    data-testid="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    disabled={sending}
                    className="flex-1 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
                  />
                  <Button
                    data-testid="send-message-btn"
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            ) : (
              <div className="p-6 border-t border-[#d4af37]/20 bg-[#00ced1]/20 flex items-center justify-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#00ced1]" />
                <span className="text-[#f8f8f8] font-medium" data-testid="completion-message">
                  Intake completed! Thank you.
                </span>
              </div>
            )}
          </Card>
        </div>
      </div>
      <a
        href={APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-2 left-1/2 -translate-x-1/2 md:left-auto md:right-5 md:!bottom-5 md:translate-x-0 z-10"
      >
        <div className="bg-white px-2 py-1 rounded-md flex items-center gap-2 shadow-sm">
          <img src="https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp" alt="BackOffice Logo" className="w-8" />
          <div className="text-black text-xs">Powered by BackOffice</div>
        </div>
      </a>
    </>
  );
};

export default IntakePage;
